import { useEffect, useMemo, useState } from 'react';
import AdminHeader from '../components/Header';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';

interface DashboardAta {
  id: number;
  numero: string;
  nome: string;
  orgao: string;
  status: string;
  visibilidade: string;
  pesquisas: number;
  anexos: unknown[];
  created_at: string | null;
  updated_at: string | null;
}

interface DashboardProduto {
  id: number;
  nome: string;
  sku: string;
  marca: string;
  estoque: number;
  vendas: number;
  ativo: boolean;
  fotos: Array<{ url?: string }>;
  variantes: Array<{ estoque?: number; ativo?: boolean }>;
  created_at: string | null;
  updated_at: string | null;
}

interface StatCardProps {
  icon: string;
  iconClass: string;
  bgClass: string;
  value: string | number;
  label: string;
  detail: React.ReactNode;
}

function StatCard({ icon, iconClass, bgClass, value, label, detail }: StatCardProps) {
  return (
    <div className="bg-white rounded-xl p-6 border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${bgClass}`}>
          <i className={`${icon} ${iconClass} text-xl`}></i>
        </div>
        <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
          Dados reais
        </span>
      </div>
      <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
      <div className="mt-3 text-xs text-gray-500">{detail}</div>
    </div>
  );
}

function isCurrentMonth(dateValue: string | null) {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
}

function formatLastSync(dateValue: string | null) {
  if (!dateValue) return 'Sem atualizacao registrada';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Sem atualizacao registrada';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function countCloudflareFiles(items: DashboardProduto[]) {
  return items.reduce((acc, produto) => {
    const cloudflareFotos = produto.fotos.filter((foto) => {
      const url = foto.url || '';
      return url.includes('r2.dev') || url.includes('cloudflarestorage.com');
    }).length;
    return acc + cloudflareFotos;
  }, 0);
}

export default function DashboardPage() {
  const [atas, setAtas] = useState<DashboardAta[]>([]);
  const [produtos, setProdutos] = useState<DashboardProduto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasSupabaseSession, setHasSupabaseSession] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function loadDashboard() {
      if (!isSupabaseConfigured || !supabase) {
        if (mounted) {
          setError('Supabase nao esta configurado. O dashboard nao usa dados mockados.');
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError('');

        const { data: sessionData } = await supabase.auth.getSession();
        const isAuthenticated = Boolean(sessionData.session);

        const [atasResult, produtosResult] = await Promise.all([
          supabase
            .from('donnos_atas')
            .select('id, numero, nome, orgao, status, visibilidade, pesquisas, anexos, created_at, updated_at')
            .order('id', { ascending: true }),
          supabase
            .from('donnos_produtos')
            .select('id, nome, sku, marca, estoque, vendas, ativo, fotos, variantes, created_at, updated_at')
            .order('id', { ascending: true }),
        ]);

        if (atasResult.error) throw atasResult.error;
        if (produtosResult.error) throw produtosResult.error;

        if (mounted) {
          setHasSupabaseSession(isAuthenticated);
          setAtas((atasResult.data || []) as DashboardAta[]);
          setProdutos((produtosResult.data || []) as DashboardProduto[]);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err instanceof Error
              ? `Nao foi possivel carregar os dados reais do dashboard: ${err.message}`
              : 'Nao foi possivel carregar os dados reais do dashboard.',
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  const metrics = useMemo(() => {
    const atasAtivas = atas.filter((ata) => ata.status === 'Ativa').length;
    const atasEncerradas = atas.filter((ata) => ata.status === 'Encerrada').length;
    const atasOcultas = atas.filter((ata) => ata.visibilidade === 'Oculta').length;
    const totalPesquisas = atas.reduce((acc, ata) => acc + Number(ata.pesquisas || 0), 0);
    const anexosAtas = atas.reduce((acc, ata) => acc + (Array.isArray(ata.anexos) ? ata.anexos.length : 0), 0);

    const produtosAtivos = produtos.filter((produto) => produto.ativo).length;
    const produtosInativos = produtos.filter((produto) => !produto.ativo).length;
    const estoqueTotal = produtos.reduce((acc, produto) => acc + Number(produto.estoque || 0), 0);
    const produtosEstoqueBaixo = produtos.filter((produto) => produto.ativo && Number(produto.estoque || 0) < 20).length;
    const totalVendas = produtos.reduce((acc, produto) => acc + Number(produto.vendas || 0), 0);
    const fotosCloudflare = countCloudflareFiles(produtos);

    const registrosEsteMes =
      atas.filter((ata) => isCurrentMonth(ata.created_at)).length +
      produtos.filter((produto) => isCurrentMonth(produto.created_at)).length;

    const lastSync = [...atas, ...produtos]
      .map((item) => item.updated_at || item.created_at)
      .filter(Boolean)
      .sort()
      .at(-1) || null;

    return {
      atasAtivas,
      atasEncerradas,
      atasOcultas,
      totalPesquisas,
      anexosAtas,
      produtosAtivos,
      produtosInativos,
      estoqueTotal,
      produtosEstoqueBaixo,
      totalVendas,
      fotosCloudflare,
      registrosEsteMes,
      lastSync,
    };
  }, [atas, produtos]);

  const atasMaisPesquisadas = useMemo(
    () => [...atas].sort((a, b) => Number(b.pesquisas || 0) - Number(a.pesquisas || 0)).slice(0, 5),
    [atas],
  );

  const produtosMaisConsultados = useMemo(
    () => [...produtos].sort((a, b) => Number(b.vendas || 0) - Number(a.vendas || 0)).slice(0, 5),
    [produtos],
  );

  const maxPesquisas = Math.max(1, ...atasMaisPesquisadas.map((ata) => Number(ata.pesquisas || 0)));
  const maxVendas = Math.max(1, ...produtosMaisConsultados.map((produto) => Number(produto.vendas || 0)));

  return (
    <div>
      <AdminHeader title="Dashboard" subtitle="Indicadores reais do catalogo Donnos" />

      <div className="p-8">
        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-100 animate-pulse">
                <div className="w-12 h-12 rounded-lg bg-gray-100 mb-4"></div>
                <div className="h-8 w-24 bg-gray-100 rounded mb-2"></div>
                <div className="h-4 w-32 bg-gray-100 rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon="ri-file-list-3-line"
              iconClass="text-donnos-green"
              bgClass="bg-donnos-green/10"
              value={atas.length}
              label="Atas cadastradas"
              detail={
                <div className="flex flex-wrap gap-x-2 gap-y-1">
                  <span className="text-donnos-green font-medium">{metrics.atasAtivas} ativas</span>
                  <span>{metrics.atasEncerradas} encerradas</span>
                  <span>{metrics.atasOcultas} ocultas</span>
                </div>
              }
            />

            <StatCard
              icon="ri-search-line"
              iconClass="text-blue-500"
              bgClass="bg-blue-50"
              value={metrics.totalPesquisas}
              label="Pesquisas registradas em atas"
              detail={`${metrics.anexosAtas} anexo(s) vinculados nas atas`}
            />

            <StatCard
              icon="ri-shopping-bag-3-line"
              iconClass="text-amber-500"
              bgClass="bg-amber-50"
              value={produtos.length}
              label="Produtos cadastrados"
              detail={
                <div className="flex flex-wrap gap-x-2 gap-y-1">
                  <span className="text-donnos-green font-medium">{metrics.produtosAtivos} ativos</span>
                  <span>{metrics.produtosInativos} inativos</span>
                  <span className={metrics.produtosEstoqueBaixo > 0 ? 'text-red-500 font-medium' : ''}>
                    {metrics.produtosEstoqueBaixo} estoque baixo
                  </span>
                </div>
              }
            />

            <StatCard
              icon="ri-database-2-line"
              iconClass="text-orange-500"
              bgClass="bg-orange-50"
              value={metrics.estoqueTotal}
              label="Unidades em estoque"
              detail={`${metrics.totalVendas} consulta(s)/venda(s) registradas - ${metrics.fotosCloudflare} foto(s) no Cloudflare R2`}
            />
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase">Origem dos dados</p>
            <p className="mt-2 text-sm font-medium text-gray-900">Supabase - tabelas reais</p>
            <p className="mt-1 text-xs text-gray-500">Sem fallback para mocks no dashboard.</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase">Novos registros no mes</p>
            <p className="mt-2 text-2xl font-bold text-gray-900">{metrics.registrosEsteMes}</p>
            <p className="mt-1 text-xs text-gray-500">Calculado por `created_at`.</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase">Data da ultima insercao/atualizacao de produto ou ATA</p>
            <p className="mt-2 text-sm font-medium text-gray-900">{formatLastSync(metrics.lastSync)}</p>
            <p className="mt-1 text-xs text-gray-500">Calculado por `updated_at` ou `created_at`.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Atas mais pesquisadas</h3>
              <p className="text-sm text-gray-500 mt-1">Ranking calculado pelo campo real `pesquisas`.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase px-6 py-3">Numero</th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase px-6 py-3">Orgao</th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase px-6 py-3">Pesquisas</th>
                  </tr>
                </thead>
                <tbody>
                  {atasMaisPesquisadas.length > 0 ? (
                    atasMaisPesquisadas.map((ata) => (
                      <tr key={ata.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-3">
                          <span className="text-sm font-medium text-gray-900">{ata.numero}</span>
                          <p className="text-xs text-gray-500">{ata.nome}</p>
                        </td>
                        <td className="px-6 py-3">
                          <span className="text-sm text-gray-600">{ata.orgao || 'Nao informado'}</span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-donnos-green rounded-full"
                                style={{ width: `${(Number(ata.pesquisas || 0) / maxPesquisas) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-gray-900">{Number(ata.pesquisas || 0)}</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-sm text-gray-500">
                        Nenhuma ata real encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Produtos com mais registros</h3>
              <p className="text-sm text-gray-500 mt-1">Ranking calculado pelo campo real `vendas`.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase px-6 py-3">Produto</th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase px-6 py-3">Estoque</th>
                    <th className="text-left text-xs font-semibold text-gray-600 uppercase px-6 py-3">Registros</th>
                  </tr>
                </thead>
                <tbody>
                  {produtosMaisConsultados.length > 0 ? (
                    produtosMaisConsultados.map((produto) => (
                      <tr key={produto.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-3">
                          <span className="text-sm font-medium text-gray-900">{produto.nome}</span>
                          <p className="text-xs text-gray-500">{produto.sku} - {produto.marca || 'Sem marca'}</p>
                        </td>
                        <td className="px-6 py-3">
                          <span className={Number(produto.estoque || 0) < 20 ? 'text-sm font-medium text-red-500' : 'text-sm text-gray-600'}>
                            {Number(produto.estoque || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-amber-500 rounded-full"
                                style={{ width: `${(Number(produto.vendas || 0) / maxVendas) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-gray-900">{Number(produto.vendas || 0)}</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-sm text-gray-500">
                        Nenhum produto real encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
